import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import personalFeedApiClient, {
  PersonalFeedHistoryInput,
  PersonalFeedHistory,
} from "../../../api/personalFeed";
import { DateTime } from "../../../api/calendarEvent";

const DEFAULT_LIMIT = 20;

export type UsePersonalFeedHistoryParams = {
  limit?: number;
  date?: DateTime;
};

const usePersonalFeedHistory = ({
  limit = DEFAULT_LIMIT,
  date,
}: UsePersonalFeedHistoryParams = {}) => {
  const queryClient = useQueryClient();

  const { data, fetchNextPage, hasNextPage, ...rest } = useInfiniteQuery<
    PersonalFeedHistory[],
    Error
  >({
    queryKey: ["personal-feed-history", limit, date],
    queryFn: async ({ pageParam }) => {
      const input: PersonalFeedHistoryInput = {
        skip: (pageParam as { skip?: number })?.skip || 0,
        limit,
        date,
      };

      const result = await personalFeedApiClient.getPersonalFeedHistory({
        ...input,
      });

      result.forEach((feedItem) => {
        queryClient.setQueryData(
          ["calendar-event", feedItem.calendarEvent.id],
          feedItem.calendarEvent,
        );
      });

      return result.map((item) => ({
        ...item.calendarEvent,
        displayedKlydos: item.displayedKlydos || [],
      }));
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentLength = allPages.flat().length;
      return lastPage.length < limit ? undefined : { skip: currentLength };
    },
    initialPageParam: { skip: 0 },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const flatData = data?.pages.flat();

  return {
    data: flatData,
    fetchNextPage,
    hasNextPage,
    ...rest,
  };
};

export default usePersonalFeedHistory;
